import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import api from '../../api/axios';

// Modal-layer cache: avoid re-hydrating the same job repeatedly.
const hydratedJobCache = new Map();

const formatSalary = (job) => {
  const min = job?.salary?.min ?? job?.jobMinSalary;
  const max = job?.salary?.max ?? job?.jobMaxSalary;
  if (min && max) return `$${Number(min).toLocaleString()} - $${Number(max).toLocaleString()}`;
  if (max) return `Up to $${Number(max).toLocaleString()}`;
  if (min) return `From $${Number(min).toLocaleString()}`;
  return 'Compensation TBD';
};

const getJobTitle = (job) => job?.title || job?.jobPosition || 'Untitled role';
const getCompany = (job) => job?.company || job?.companyName || 'Unknown company';
const getLocation = (job) => job?.location || job?.jobLocation || 'Location not specified';
const getDescription = (job) => job?.description || job?.jobDesc || 'No description available.';

const AIAssistantJobModal = ({ isOpen, onClose, job }) => {
  const jobsFromStore = useSelector((state) => state.jobs?.enrichedJobsArray || []);
  const companiesFromStore = useSelector((state) => state.companies?.companies || []);
  const [hydratedJob, setHydratedJob] = useState(job);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    setHydratedJob(job);
  }, [job]);

  useEffect(() => {
    const hydrateDetails = async () => {
      if (!isOpen || !job) return;

      const jobId = job?._id || job?.id;
      const cacheKey = jobId || `${normalizeForMatch(getJobTitle(job))}::${normalizeForMatch(getCompany(job))}`;

      const cachedJob = hydratedJobCache.get(cacheKey);
      if (cachedJob) {
        setHydratedJob(cachedJob);
        return;
      }

      const hasRichDetails = Boolean(job?.jobDesc || job?.description) && Boolean(job?.companyId);
      if (hasRichDetails) {
        hydratedJobCache.set(cacheKey, job);
        return;
      }

      const storeMatch = jobsFromStore.find((j) => {
        const sameId = jobId && (j?._id === jobId || j?.id === jobId);
        const sameByTitleCompany =
          normalizeForMatch(getJobTitle(j)) === normalizeForMatch(getJobTitle(job)) &&
          normalizeForMatch(getCompany(j)) === normalizeForMatch(getCompany(job));
        return sameId || sameByTitleCompany;
      });

      if (storeMatch) {
        setHydratedJob(storeMatch);
        hydratedJobCache.set(cacheKey, storeMatch);
        return;
      }

      setLoadingDetails(true);
      try {
        // Lazy fetch full details only when user opens a card.
        const response = await api.get('/api/jobs/fetchjobs?limit=50');
        const jobs = response?.data?.jobs || response?.data || [];
        const apiMatch = jobs.find((j) => {
          const sameId = jobId && (j?._id === jobId || j?.id === jobId);
          const sameByTitleCompany =
            normalizeForMatch(getJobTitle(j)) === normalizeForMatch(getJobTitle(job)) &&
            normalizeForMatch(getCompany(j)) === normalizeForMatch(getCompany(job));
          return sameId || sameByTitleCompany;
        });
        if (apiMatch) {
          setHydratedJob(apiMatch);
          hydratedJobCache.set(cacheKey, apiMatch);
        }
      } catch (error) {
        // Keep lightweight data if hydration fails.
      } finally {
        setLoadingDetails(false);
      }
    };

    hydrateDetails();
  }, [isOpen, job, jobsFromStore]);

  const normalizedCompanyId = useMemo(() => {
    const c = hydratedJob?.companyId;
    if (!c) return null;
    return typeof c === 'object' ? c._id : c;
  }, [hydratedJob]);

  const companyFromStore = useMemo(() => {
    if (!normalizedCompanyId) return null;
    return companiesFromStore.find((company) => company?._id === normalizedCompanyId) || null;
  }, [companiesFromStore, normalizedCompanyId]);

  const displayJob = useMemo(() => {
    if (!companyFromStore) return hydratedJob;
    return {
      ...hydratedJob,
      companyId: typeof hydratedJob?.companyId === 'object'
        ? { ...companyFromStore, ...hydratedJob.companyId }
        : companyFromStore
    };
  }, [hydratedJob, companyFromStore]);

  const requiredSkills = displayJob?.requiredSkills || displayJob?.skills || [];
  const fitScore = displayJob?.referralLikelihood || displayJob?.companyId?.fitScore;

  if (!isOpen || !job) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/30 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Job details"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-hidden rounded-t-3xl sm:rounded-3xl bg-white border border-black/[0.06] shadow-ios-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 sm:px-6 pt-4 pb-3 border-b border-black/[0.06] bg-white/90 backdrop-blur-xl">
          <div className="h-1 w-10 mx-auto rounded-full bg-[#D1D1D6] sm:hidden mb-3" />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-[20px] sm:text-[22px] font-semibold tracking-[-0.02em] text-[#1C1C1E] text-left">
                {getJobTitle(displayJob)}
              </h2>
              <p className="mt-1 text-[15px] text-[#636366] text-left">
                {getCompany(displayJob)} - {getLocation(displayJob)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-full h-9 w-9 bg-[#F2F2F7] text-[#3A3A3C] active:scale-95 transition-transform"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-4 pr-4 sm:pr-5 space-y-4 max-h-[calc(92vh-150px)]">
          {loadingDetails && (
            <div className="rounded-xl border border-[#007AFF]/20 bg-[#007AFF]/8 px-3 py-2 text-[13px] text-[#0A84FF] text-left">
              Loading full job details...
            </div>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl bg-[#F2F2F7] px-3.5 py-3 text-left">
              <p className="text-[12px] text-[#8E8E93]">Compensation</p>
              <p className="text-[14px] font-semibold text-[#1C1C1E]">{formatSalary(displayJob)}</p>
            </div>
            <div className="rounded-2xl bg-[#F2F2F7] px-3.5 py-3 text-left">
              <p className="text-[12px] text-[#8E8E93]">Fit</p>
              <p className="text-[14px] font-semibold text-[#1C1C1E]">
                {fitScore ? `${Math.round(fitScore * 100)}% match` : 'Pending'}
              </p>
            </div>
          </div>

          {requiredSkills.length > 0 && (
            <section>
              <h3 className="text-[13px] font-semibold text-[#636366] mb-2 text-left">Skills</h3>
              <div className="flex flex-wrap gap-1.5">
                {requiredSkills.slice(0, 10).map((skill, idx) => (
                  <span
                    key={`${skill}-${idx}`}
                    className="rounded-lg bg-[#F2F2F7] px-2.5 py-1 text-[12px] font-medium text-[#3A3A3C]"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className="text-[13px] font-semibold text-[#636366] mb-2 text-left">About this role</h3>
            <p className="rounded-2xl bg-[#F2F2F7] px-3.5 py-3 text-[14px] leading-relaxed text-[#1C1C1E] whitespace-pre-wrap text-left">
              {getDescription(displayJob)}
            </p>
          </section>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-black/[0.06] bg-white">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 rounded-xl bg-[#F2F2F7] text-[15px] font-medium text-[#3A3A3C] active:scale-[0.98] transition-transform"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeForMatch = (value) => (value || '').toString().trim().toLowerCase();

export default AIAssistantJobModal;
